<template>
    <div class="engagement-rings">
        <PageContentLoader v-if="fetching && !data" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="executeQuery"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <div v-else-if="data">
            <ProductBreadcrumb :breadcrumbs />

            <div
                v-qa="'before-filters-content'"
                class="before-filters-content"
            >
                <ThTitle
                    v-if="facetedTitle && !props.recommendationsLayout"
                    alignment="CENTER"
                    color="var(--blue)"
                    tag="H1"
                >
                    {{ facetedTitle }}
                </ThTitle>

                <CmsBlocks
                    v-else
                    :data="data.pageProductListing?.beforeFilters || []"
                />
            </div>

            <RtgBanner />

            <NameSearch
                v-if="isAdmin"
                v-model="search"
            />

            <ProductGrid
                ref="gridNode"
                :active-filter-count
                :banner1="data.pageProductListing?.banner1 || undefined"
                :banner2="data.pageProductListing?.banner2 || undefined"
                :data="data.catalogueEngagementRings"
                :fetching
                :filter-chips
                :full-grid-at="perPage"
                :get-route-sort="filterRoutes.getRouteSort"
                :page
                :results-count="data.catalogueEngagementRingsAggregate.count"
                :route-reset="filterRoutes.routeReset"
                :sort-by="sortByWithDefault"
                :track-clicked
                is-e-r
                :recommendations-layout="props.recommendationsLayout"
                @open-filters-modal="openFilterModal"
            />

            <Pagination
                v-if="data.catalogueEngagementRingsAggregate.count > 0"
                :count="data.catalogueEngagementRingsAggregate.count"
                :current="page"
                :get-route-page="filterRoutes.getRoutePage"
                :limit="perPage"
                :range="2"
                scroll-into-view-selector=".product-grid"
            />

            <RtgBanner />

            <FacetedContent
                v-if="facetedContent"
                :content="facetedContent"
            />

            <CmsBlocks
                v-else
                :data="data.pageProductListing?.afterGrid || []"
            />
        </div>

        <EngagementRingFilters
            v-if="
                data?.catalogueEngagementRingFilters &&
                !error &&
                isFilterModalRendered
            "
            v-model:values="filterInput"
            v-qa="'engagement-rings-filters'"
            :chips="filterChips"
            :disabled="fetching"
            :filter-routes
            :filters="data.catalogueEngagementRingFilters"
            :is-open="isFilterModalOpen"
            :loading="fetching"
            :result-count="data.catalogueEngagementRingsAggregate.count"
            @close="closeFilterModal"
        />
    </div>
</template>

<script lang="ts" setup>
import {
    computed,
    type ComputedRef,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    toRef,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { useRouter } from 'vue-router'

import {
    CatalogueSortOrder,
    type EngagementRingBrowseQueryVariables,
    type EngagementRingDesignFeatureLabelCode,
    type EngagementRingDesignStyleLabelCode,
    type EngagementRingStyleLabelCode,
    type MetalCode,
    type ProductPageFacetedContentInput,
    type StoneShapeCode,
} from '@/graphql/api'

import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRoutePathWatcher } from '@/composables/route-path-watcher.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'
import { usePageCount } from '@/composables/page-count.ts'
import { useTranslator } from '@/i18n'
import { useRegionPlugin } from '@/plugins/region'
import { useMeta } from '@/composables/app/useMeta'
import { useResponseError } from '@/helpers/errors'
import { useFacetedContent } from '@/domain/product/useFacetedContent'
import { useEngagementRingsFilters } from './composables/useEngagementRingsFilters'
import { useRoutes } from '@/router/routes'
import { useProductListingPage } from '@/views/product/useProductListingPage'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useProductListAnalytics } from '../composables/analytics/useProductListAnalytics'
import { useFocusFilterButton } from '../composables/ui/useFocusFilterButton'
import { useFilterModal } from '@/composables/filter-modal.ts'
import { useEngagementRingBrowseQuery } from '@/views/product/engagement-ring/EngagementRings.generated.ts'
import { useFilterButtonClient } from '@/components/product/filter-button/useFilterButton'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import ProductGrid from '@/components/rings/product-grid/ProductGrid.vue'
import ProductBreadcrumb from '@/components/rings/ProductBreadcrumbs.vue'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import RtgBanner from '@/views/product/engagement-ring/RtgBanner.vue'

const EngagementRingFilters = defineAsyncComponent({
    loader: () =>
        import('@/components/rings/engagement/EngagementRingFilters.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFiveHundred = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFiveHundred.vue'),
    hydrate: hydrateOnVisible(),
})

const Pagination = defineAsyncComponent({
    loader: () => import('@/components/ui/Pagination.vue'),
    hydrate: hydrateOnVisible(),
})

const FacetedContent = defineAsyncComponent({
    loader: () => import('@/components/product/FacetedContent.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

const NameSearch = defineAsyncComponent({
    loader: () => import('@/components/rings/NameSearch.vue'),
    hydrate: hydrateOnIdle(),
})

export interface Props {
    slug: string[]
    metal?: MetalCode
    ringStyle?: EngagementRingStyleLabelCode
    shape?: StoneShapeCode
    designStyle?: EngagementRingDesignStyleLabelCode
    page: number
    designFeatures?: EngagementRingDesignFeatureLabelCode[]
    priceMin?: number
    priceMax?: number
    sortBy?: CatalogueSortOrder
    recommendationsLayout?: boolean
}

const props = defineProps<Props>()

const sortByWithDefault = toRef(
    () => props.sortBy ?? CatalogueSortOrder.Recommended
)

const perPage = 32

const isAdmin = useIsAdmin()
const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const routes = useRoutes()
const router = useRouter()
const currency = useCurrencyInput()
const { t } = useI18n()

const pathWatcher = useRoutePathWatcher(
    () => {
        return router.resolve(
            routes.engagementRings({
                metal: props.metal,
                ringStyle: props.ringStyle,
                shape: props.shape,
                designStyle: props.designStyle,
                page: props.page,
            })
        )
    },
    () => {
        return router.resolve(
            routes.engagementRings({
                metal: props.metal,
                ringStyle: props.ringStyle,
                shape: props.shape,
                designStyle: props.designStyle,
                designFeatures: props.designFeatures,
                priceMin: props.priceMin,
                priceMax: props.priceMax,
                page: props.page,
                sortBy: props.sortBy,
            })
        ).fullPath
    },
    301
)

const facetedContentInput = computed(() => ({
    engagementRings: {
        designStyle: props.designStyle,
        metal: props.metal,
        ringStyle: props.ringStyle,
        shape: props.shape,
    },
})) satisfies ComputedRef<ProductPageFacetedContentInput>

const activeFacetedFilterCount = computed(
    () =>
        Object.values(facetedContentInput.value.engagementRings).filter(Boolean)
            .length
)

const { filterRoutes, filterInput, activeFilterCount, filterChips, search } =
    useEngagementRingsFilters(
        props,
        toRef(() => data.value?.catalogueEngagementRingFilters)
    )

const {
    isFilterModalOpen,
    isFilterModalRendered,
    openFilterModal,
    closeFilterModal,
} = useFilterModal()

useFilterButtonClient(openFilterModal, activeFilterCount)

const variables = computed<EngagementRingBrowseQueryVariables>(() => ({
    filter: filterInput.value,
    currency: currency.value,
    region: regionCode.value,
    page: props.page,
    locale: locale.value,
    path: props.recommendationsLayout
        ? 'engagement-rings-recommendations'
        : 'engagement-rings',
    facetedContentInput: facetedContentInput.value,
    limit: perPage,
    sortBy: sortByWithDefault.value,
}))

const response = useEngagementRingBrowseQuery({ variables })

const pageCount = usePageCount(
    () => response.data.value?.catalogueEngagementRingsAggregate.count ?? 0,
    () => perPage
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(filterRoutes.getRoutePage(1)).fullPath
    },
    302
)

await response

const { fetching, stale, data, error, executeQuery } = response

const content = toRef(() => data.value?.pageProductListingFacetedContent)

const catalogue = toRef(() => data.value?.catalogueEngagementRings)

const {
    facetedTitle,
    facetedContent,
    facetedMetaDescription,
    facetedMetaTitle,
} = useFacetedContent(
    toRef(() => ({
        type: 'engagement-rings' as const,
        filters: data.value?.catalogueEngagementRingFilters,
        filterValues: filterInput.value,
    })),
    content,
    activeFacetedFilterCount,
    toRef(() => data.value?.pageProductListing?.meta)
)

const responseErrors = useResponseError(response)

const facets = computed(() => ({
    shape: data.value?.catalogueEngagementRingFilters?.centreStoneShapes.find(
        (s) => s.shape.systemCode === props.shape
    ),
    metal: data.value?.catalogueEngagementRingFilters?.primaryMetals.find(
        (v) => v.metal.systemCode === props.metal
    ),
    ringStyle: data.value?.catalogueEngagementRingFilters?.ringStyles.find(
        (s) => s.label.systemCode === props.ringStyle
    ),
    designStyle: data.value?.catalogueEngagementRingFilters?.designStyles.find(
        (s) => s.label.systemCode === props.designStyle
    ),
}))

const { trackClicked } = useProductListAnalytics(
    catalogue,
    filterInput,
    computed(() => [
        facets.value.shape?.shape.translation.name,
        facets.value.metal?.metal.translation.name,
        facets.value.ringStyle?.label.translation.name,
        facets.value.designStyle?.label.translation.name,
        data.value?.catalogueEngagementRingFilters?.designFeatures.find((f) =>
            props.designFeatures?.includes(f.label.systemCode)
        )?.label.translation.name,
    ]),
    sortByWithDefault,
    toRef(() => props.page)
)

const { breadcrumbs } = useProductListingPage({
    facets,
    resetRouteGetter: (facets) =>
        routes.engagementRings({
            shape: facets.shape?.shape.systemCode,
            metal: facets.metal?.metal.systemCode,
            ringStyle: facets.ringStyle?.label.systemCode,
            designStyle: facets.designStyle?.label.systemCode,
        }),
    breadcrumbsGetter: (facets) => {
        const home: Breadcrumb = {
            title: 'Home',
            to: routes.home(),
        }
        const engagementRings: Breadcrumb = {
            title: 'Engagement Rings',
            to: routes.engagementRings({}),
        }

        return computed(() => {
            const breadcrumbs: Breadcrumb[] = [home, engagementRings]
            const routeParams: Parameters<typeof routes.engagementRings>[0] = {}

            if (facets.value.metal) {
                routeParams.metal = facets.value.metal?.metal.systemCode

                breadcrumbs.push({
                    title: facets.value.metal.metal.translation.name,
                    to: routes.engagementRings(routeParams),
                })
            }

            if (facets.value.ringStyle) {
                routeParams.ringStyle = facets.value.ringStyle?.label.systemCode
                breadcrumbs.push({
                    title: facets.value.ringStyle.label.translation.name,
                    to: routes.engagementRings(routeParams),
                })
            }

            if (facets.value.shape) {
                routeParams.shape = facets.value.shape.shape.systemCode
                breadcrumbs.push({
                    title: facets.value.shape.shape.translation.name,
                    to: routes.engagementRings(routeParams),
                })
            }

            if (facets.value.designStyle) {
                routeParams.designStyle =
                    facets.value.designStyle.label.systemCode

                breadcrumbs.push({
                    title: facets.value.designStyle.label.translation.name,
                    to: routes.engagementRings(routeParams),
                })
            }

            return breadcrumbs
        })
    },
})

useMeta(
    {
        title: () =>
            props.recommendationsLayout &&
            data.value?.pageProductListing?.meta?.title
                ? data.value?.pageProductListing?.meta?.title
                : facetedMetaTitle.value,
        description: () =>
            props.recommendationsLayout &&
            data.value?.pageProductListing?.meta?.description
                ? data.value?.pageProductListing?.meta?.description
                : facetedMetaDescription.value,
    },
    data,
    fetching,
    stale
)

useHead({
    meta: () => {
        if (activeFacetedFilterCount.value > 2 || props.recommendationsLayout) {
            return [
                {
                    name: 'robots',
                    content: 'noindex, nofollow',
                },
            ]
        }

        return []
    },
})

const { gridNode } = useFocusFilterButton(isFilterModalOpen)

await pathWatcher()
await pageWatcher()
</script>

<style lang="scss" scoped>
.engagement-rings {
    .pagination {
        & + .catalogue-switch-wrapper {
            margin-bottom: 40px;
        }
    }
}
</style>
